/**
 * GuestsPage - Full-featured guest management
 * Features: Add/Edit guest, Import CSV, Bulk select, Send messages, Filter/Search
 */

import React, { useState, useMemo } from 'react';
import { guestService } from '../../services/api';
import toast from 'react-hot-toast';
import CustomMessageModal from '../../components/CustomMessageModal';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';

// Helpers
const initials = (n) => n ? n.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase() : '??';

// Icon component
const Icon = ({ name, size = 16 }) => {
  const icons = {
    check: <><polyline points="20 6 9 17 4 12"/></>,
    plus: <><path d="M5 12h14M12 5v14"/></>,
    search: <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></>,
    x: <><path d="M18 6 6 18M6 6l12 12"/></>,
    trash: <><path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></>,
    upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></>,
    download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></>,
    message: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></>,
    send: <><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></>,
    user: <><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 0 0-16 0"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name] || icons.user}
    </svg>
  );
};

// Modal wrapper
const Modal = ({ isOpen, onClose, title, children, width = 480 }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 10000
    }}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, width, maxWidth: '90vw', maxHeight: '90vh',
        overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--c-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <Icon name="x" size={20} />
          </button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
};

// Form field component
const Field = ({ label, children, required }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--c-fg-muted)' }}>
      {label} {required && <span style={{ color: 'var(--c-destructive)' }}>*</span>}
    </label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid var(--c-border)',
  borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit'
};

// ============ GUESTS PAGE ============
function GuestsPage({ event, onRefresh, eventId, tierUsage, refreshTierUsage }) {
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [guestModal, setGuestModal] = useState({ open: false, guest: null });
  const [importModal, setImportModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Guest form state
  const [guestForm, setGuestForm] = useState({
    firstName: '', lastName: '', rsvp: '', whatsappNumber: '', email: '', group: '',
    plusOnes: 0, dietaryRestrictions: '', arrivalDate: '', arrivalTime: '',
    departureDate: '', departureTime: '', roomAssigned: ''
  });

  // Import state
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);

  // Message modal state
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  // Delete confirmation state: { type: 'single' | 'bulk', guest?: object }
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Phone validation
  const [phoneError, setPhoneError] = useState('');
  const validatePhone = (value) => {
    if (!value || !value.trim()) { setPhoneError(''); return; }
    const clean = value.replace(/[^\d+]/g, '');
    const normalized = clean.startsWith('+') ? clean : (clean.length === 10 ? '+1' + clean : '+' + clean);
    if (!/^\+\d{10,15}$/.test(normalized)) {
      setPhoneError('Must be international format (e.g. +919833232389)');
    } else {
      setPhoneError('');
    }
  };

  const guests = event?.guests || [];

  // Filter and search guests
  const filteredGuests = useMemo(() => {
    let result = guests;

    // Filter by tab (Note: default rsvp in database is 'unassigned')
    if (tab === 'confirmed') result = result.filter(g => g.rsvp === 'yes');
    else if (tab === 'pending') result = result.filter(g => g.rsvp === 'pending');
    else if (tab === 'notinvited') result = result.filter(g => g.rsvp === 'unassigned');
    else if (tab === 'declined') result = result.filter(g => g.rsvp === 'no');

    // Search
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(g =>
        g.name?.toLowerCase().includes(q) ||
        g.whatsappNumber?.includes(q) ||
        g.email?.toLowerCase().includes(q)
      );
    }

    return result;
  }, [guests, tab, search]);

  const withPlusOnes = (filter) => guests.filter(filter).reduce((sum, g) => sum + 1 + (g.plusOnes || 0), 0);
  const counts = {
    all: guests.reduce((sum, g) => sum + 1 + (g.plusOnes || 0), 0),
    confirmed: withPlusOnes(g => g.rsvp === 'yes'),
    pending: withPlusOnes(g => g.rsvp === 'pending'),
    notinvited: withPlusOnes(g => g.rsvp === 'unassigned'),
    declined: withPlusOnes(g => g.rsvp === 'no'),
  };

  const rsvpLabel = (rsvp) => {
    if (rsvp === 'yes') return 'confirmed';
    if (rsvp === 'no') return 'declined';
    if (rsvp === 'pending') return 'pending';
    return 'not-invited';
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredGuests.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredGuests.map(g => g._id)));
    }
  };

  // Toggle single selection
  const toggleSelect = (id, e) => {
    e.stopPropagation();
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Open guest modal
  const openGuestModal = (guest = null) => {
    if (guest) {
      const nameParts = (guest.name || '').trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      setGuestForm({
        firstName,
        lastName,
        rsvp: guest.rsvp || '',
        whatsappNumber: guest.whatsappNumber || '',
        email: guest.email || '',
        group: guest.group || '',
        plusOnes: guest.plusOnes || 0,
        dietaryRestrictions: guest.dietaryRestrictions || '',
        arrivalDate: guest.arrivalDate?.split('T')[0] || '',
        arrivalTime: guest.arrivalTime || '',
        departureDate: guest.departureDate?.split('T')[0] || '',
        departureTime: guest.departureTime || '',
        roomAssigned: guest.roomAssigned || '',
      });
    } else {
      setGuestForm({
        firstName: '', lastName: '', rsvp: '', whatsappNumber: '', email: '', group: '',
        plusOnes: 0, dietaryRestrictions: '', arrivalDate: '', arrivalTime: '',
        departureDate: '', departureTime: '', roomAssigned: ''
      });
    }
    setPhoneError('');
    setGuestModal({ open: true, guest });
  };

  // Save guest
  const saveGuest = async () => {
    if (!guestForm.firstName.trim()) {
      toast.error('First name is required');
      return;
    }
    if (phoneError) {
      toast.error('Please fix the phone number before saving');
      return;
    }
    const mergedName = [guestForm.firstName.trim(), guestForm.lastName.trim()].filter(Boolean).join(' ');
    const { firstName, lastName, ...rest } = guestForm;
    const payload = { ...rest, name: mergedName };
    setSaving(true);
    try {
      if (guestModal.guest?._id) {
        await guestService.updateGuest(guestModal.guest._id, payload);
        toast.success('Guest updated');
      } else {
        await guestService.addGuest(eventId, payload);
        toast.success('Guest added');
      }
      setGuestModal({ open: false, guest: null });
      setSelected(null);
      onRefresh();
      refreshTierUsage?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save guest');
    } finally {
      setSaving(false);
    }
  };

  // Delete guest
  const deleteGuest = async () => {
    if (!guestModal.guest?._id) return;
    setSaving(true);
    try {
      await guestService.deleteGuest(guestModal.guest._id);
      toast.success('Guest deleted');
      setGuestModal({ open: false, guest: null });
      setSelected(null);
      setDeleteConfirm(null);
      onRefresh();
      refreshTierUsage?.();
    } catch (err) {
      toast.error('Failed to delete guest');
    } finally {
      setSaving(false);
    }
  };

  // Bulk delete
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setSaving(true);
    try {
      const count = selectedIds.size;
      await guestService.bulkDeleteGuests(Array.from(selectedIds));
      toast.success(`${count} guests deleted`);
      setSelectedIds(new Set());
      setDeleteConfirm(null);
      onRefresh();
      refreshTierUsage?.();
    } catch (err) {
      toast.error('Failed to delete guests');
    } finally {
      setSaving(false);
    }
  };

  // Handle CSV file
  const handleCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvFile(file);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      const preview = lines.slice(0, 5).map(line => {
        const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
        return { name: cols[0], whatsappNumber: cols[1], email: cols[2] };
      });
      setCsvPreview(preview);
    };
    reader.readAsText(file);
  };

  // Import CSV
  const importCsv = async () => {
    if (!csvFile || csvPreview.length === 0) return;
    setSaving(true);
    try {
      // Parse full CSV and use bulkAddGuests
      const reader = new FileReader();
      reader.onload = async (evt) => {
        const text = evt.target.result;
        const lines = text.split('\n').filter(l => l.trim());
        // Skip header if first line looks like headers
        const startIdx = lines[0]?.toLowerCase().includes('name') ? 1 : 0;
        const guests = lines.slice(startIdx).map(line => {
          const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
          return { name: cols[0], whatsappNumber: cols[1] || '', email: cols[2] || '' };
        }).filter(g => g.name);

        await guestService.bulkAddGuests(eventId, guests, true);
        toast.success(`${guests.length} guests imported`);
        setImportModal(false);
        setCsvFile(null);
        setCsvPreview([]);
        onRefresh();
        refreshTierUsage?.();
        setSaving(false);
      };
      reader.readAsText(csvFile);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to import');
      setSaving(false);
    }
  };

  // Export CSV
  const exportCsv = () => {
    const headers = ['Name', 'RSVP', 'WhatsApp', 'Email', 'Group', 'Plus Ones', 'Dietary', 'Arrival Date', 'Arrival Time', 'Departure Date', 'Departure Time', 'Room'];
    const rows = guests.map(g => [
      g.name, g.rsvp, g.whatsappNumber, g.email, g.group, g.plusOnes, g.dietaryRestrictions,
      g.arrivalDate, g.arrivalTime, g.departureDate, g.departureTime, g.roomAssigned
    ].map(v => `"${v || ''}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `guests-${event.name?.replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  // Get pre-selected guests for the message modal
  const getPreSelectedGuests = () => {
    if (selectedIds.size > 0) {
      return guests.filter(g => selectedIds.has(g._id));
    }
    return null; // null = use normal filter dropdown in CustomMessageModal
  };

  const guestLimitReached = tierUsage && !tierUsage.isAdmin && tierUsage.guests.remaining === 0;

  return (
    <>
      {/* Guest usage bar */}
      {tierUsage && !tierUsage.isAdmin && tierUsage.guests.max > 0 && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--c-muted)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
          <span style={{ fontWeight: 500, color: 'var(--c-fg-muted)', whiteSpace: 'nowrap' }}>
            Guests: {tierUsage.guests.used.toLocaleString()}/{tierUsage.guests.max.toLocaleString()}
          </span>
          <div style={{ flex: 1, height: 6, background: 'hsl(200 20% 90%)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 3, transition: 'width 0.3s',
              width: `${Math.min(100, (tierUsage.guests.used / tierUsage.guests.max) * 100)}%`,
              background: tierUsage.guests.remaining === 0 ? 'var(--c-destructive, #e53e3e)' : tierUsage.guests.remaining < tierUsage.guests.max * 0.1 ? '#e09200' : 'var(--c-primary)'
            }} />
          </div>
          {tierUsage.guests.remaining > 0 && (
            <span style={{ color: 'var(--c-fg-muted)', whiteSpace: 'nowrap' }}>
              {tierUsage.guests.remaining.toLocaleString()} remaining
            </span>
          )}
          {tierUsage.guests.remaining === 0 && (
            <span style={{ color: 'var(--c-destructive, #e53e3e)', fontWeight: 500, whiteSpace: 'nowrap' }}>
              Limit reached
            </span>
          )}
        </div>
      )}

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <input
            placeholder="Search by name or phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px 10px 36px', border: '1px solid var(--c-border)',
              borderRadius: 8, fontSize: 14, outline: 'none'
            }}
          />
          <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-fg-muted)', pointerEvents: 'none' }}>
            <Icon name="search" size={16} />
          </div>
        </div>

        {selectedIds.size > 0 && (
          <button className="btn-secondary" style={{ color: 'var(--c-destructive)' }} onClick={() => setDeleteConfirm({ type: 'bulk' })}>
            <Icon name="trash" size={14} /> Delete ({selectedIds.size})
          </button>
        )}

        <button className="btn-secondary" onClick={exportCsv}>
          <Icon name="download" size={14} /> Export
        </button>
        <button className="btn-secondary" onClick={() => setImportModal(true)} disabled={guestLimitReached}
          title={guestLimitReached ? 'Guest limit reached for your plan' : ''} style={guestLimitReached ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
          <Icon name="upload" size={14} /> Import
        </button>
        <button className="btn-secondary" onClick={() => setMessageModalOpen(true)}>
          <Icon name="send" size={14} /> Send Message {selectedIds.size > 0 && `(${selectedIds.size})`}
        </button>
        <button className="btn-primary" onClick={() => openGuestModal()} disabled={guestLimitReached}
          title={guestLimitReached ? 'Guest limit reached for your plan' : ''} style={guestLimitReached ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
          <Icon name="plus" size={14} /> Add Guest
        </button>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        {[
          { id: 'all', label: 'All' },
          { id: 'notinvited', label: 'Not Invited' },
          { id: 'pending', label: 'Pending' },
          { id: 'confirmed', label: 'Confirmed' },
          { id: 'declined', label: 'Declined' },
        ].map(t => (
          <div key={t.id} className={`tab ${tab === t.id ? 'is-active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label} <span className="count">{counts[t.id]}</span>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="with-side">
        <div className="main-col" style={{ paddingRight: selected ? 32 : 0 }}>
          <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
            {filteredGuests.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--c-fg-muted)' }}>
                {search ? 'No guests match your search' : 'No guests in this category'}
              </div>
            ) : (
              <table className="gtable">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.size === filteredGuests.length && filteredGuests.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th style={{ width: '30%' }}>Guest</th>
                    <th style={{ width: '20%' }}>WhatsApp</th>
                    <th style={{ width: '15%' }}>RSVP</th>
                    <th style={{ width: '20%' }}>Group</th>
                    <th style={{ width: '8%' }}>+1s</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGuests.map(g => (
                    <tr
                      key={g._id}
                      className={selected?._id === g._id ? 'is-selected' : ''}
                      onClick={() => setSelected(g)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(g._id)}
                          onChange={e => toggleSelect(g._id, e)}
                        />
                      </td>
                      <td>
                        <div className="g-name">
                          <div className="g-av">{initials(g.name)}</div>
                          <div>
                            <div style={{ fontWeight: 600 }}>{g.name}</div>
                            <div className="g-meta">{g.email || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: 13 }}>{g.whatsappNumber || '—'}</td>
                      <td><span className={`pill ${rsvpLabel(g.rsvp)}`}><span className="dot"></span>{rsvpLabel(g.rsvp)}</span></td>
                      <td style={{ fontSize: 13 }}>{g.group || '—'}</td>
                      <td>{g.plusOnes || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Side Panel */}
        {selected && (
          <div className="side">
            <button
              onClick={() => setSelected(null)}
              style={{
                position: 'absolute', top: 12, right: 12, background: 'var(--c-muted)',
                border: 'none', borderRadius: 6, width: 28, height: 28, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <Icon name="x" size={14} />
            </button>
            <div className="side-head">
              <div className="side-av">{initials(selected.name)}</div>
              <div className="side-name">{selected.name}</div>
              <div className="side-meta">{selected.email || selected.whatsappNumber || '—'}</div>
              <span className={`pill ${rsvpLabel(selected.rsvp)}`}><span className="dot"></span>{rsvpLabel(selected.rsvp)}</span>
            </div>
            <div className="side-row"><span className="k">WhatsApp</span><span className="v">{selected.whatsappNumber || '—'}</span></div>
            <div className="side-row"><span className="k">Group</span><span className="v">{selected.group || '—'}</span></div>
            <div className="side-row"><span className="k">Plus ones</span><span className="v">{selected.plusOnes || 0}</span></div>
            <div className="side-row"><span className="k">Dietary</span><span className="v">{selected.dietaryRestrictions || '—'}</span></div>
            <div className="side-row"><span className="k">Arrival</span><span className="v">{selected.arrivalDate ? `${selected.arrivalDate.split('T')[0]} ${selected.arrivalTime || ''}` : '—'}</span></div>
            <div className="side-row"><span className="k">Room</span><span className="v">{selected.roomAssigned || '—'}</span></div>
            <div className="side-actions">
              <button className="btn-secondary" onClick={() => setSelected(null)}>Close</button>
              <button className="btn-primary" onClick={() => openGuestModal(selected)}>Edit</button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Guest Modal */}
      <Modal isOpen={guestModal.open} onClose={() => setGuestModal({ open: false, guest: null })} title={guestModal.guest ? 'Edit Guest' : 'Add Guest'} width={560}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="First Name" required>
            <input style={inputStyle} value={guestForm.firstName} onChange={e => setGuestForm({...guestForm, firstName: e.target.value})} placeholder="First name" autoFocus />
          </Field>
          <Field label="Last Name">
            <input style={inputStyle} value={guestForm.lastName} onChange={e => setGuestForm({...guestForm, lastName: e.target.value})} placeholder="Last name" />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="RSVP Status">
            <select style={inputStyle} value={guestForm.rsvp} onChange={e => setGuestForm({...guestForm, rsvp: e.target.value})}>
              <option value="unassigned">Not Invited</option>
              <option value="pending">Pending</option>
              <option value="yes">Confirmed</option>
              <option value="no">Declined</option>
            </select>
          </Field>
          <Field label="Plus Ones">
            <input type="number" min="0" style={inputStyle} value={guestForm.plusOnes} onChange={e => setGuestForm({...guestForm, plusOnes: parseInt(e.target.value) || 0})} />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="WhatsApp Number">
            <input
              style={{ ...inputStyle, borderColor: phoneError ? '#d32f2f' : undefined }}
              value={guestForm.whatsappNumber}
              onChange={e => { setGuestForm({...guestForm, whatsappNumber: e.target.value}); if (phoneError) validatePhone(e.target.value); }}
              onBlur={e => validatePhone(e.target.value)}
              placeholder="+91..."
            />
            {phoneError && <div style={{ color: '#d32f2f', fontSize: 12, marginTop: 4 }}>{phoneError}</div>}
          </Field>
          <Field label="Email">
            <input type="email" style={inputStyle} value={guestForm.email} onChange={e => setGuestForm({...guestForm, email: e.target.value})} placeholder="email@example.com" />
          </Field>
        </div>
        <Field label="Group">
          <input style={inputStyle} value={guestForm.group} onChange={e => setGuestForm({...guestForm, group: e.target.value})} placeholder="e.g., Bride's side, Family" />
        </Field>
        <Field label="Dietary Restrictions">
          <input style={inputStyle} value={guestForm.dietaryRestrictions} onChange={e => setGuestForm({...guestForm, dietaryRestrictions: e.target.value})} placeholder="e.g., Vegetarian, No nuts" />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Arrival Date">
            <input type="date" style={inputStyle} value={guestForm.arrivalDate} onChange={e => setGuestForm({...guestForm, arrivalDate: e.target.value})} />
          </Field>
          <Field label="Arrival Time">
            <input type="time" style={inputStyle} value={guestForm.arrivalTime} onChange={e => setGuestForm({...guestForm, arrivalTime: e.target.value})} />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Departure Date">
            <input type="date" style={inputStyle} value={guestForm.departureDate} onChange={e => setGuestForm({...guestForm, departureDate: e.target.value})} />
          </Field>
          <Field label="Departure Time">
            <input type="time" style={inputStyle} value={guestForm.departureTime} onChange={e => setGuestForm({...guestForm, departureTime: e.target.value})} />
          </Field>
        </div>
        <Field label="Room Assigned">
          <input style={inputStyle} value={guestForm.roomAssigned} onChange={e => setGuestForm({...guestForm, roomAssigned: e.target.value})} placeholder="e.g., Room 101" />
        </Field>
        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'space-between' }}>
          <div>
            {guestModal.guest && (
              <button className="btn-secondary" style={{ color: 'var(--c-destructive)' }} onClick={() => setDeleteConfirm({ type: 'single', guest: guestModal.guest })} disabled={saving}>
                <Icon name="trash" size={14} /> Delete
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn-secondary" onClick={() => setGuestModal({ open: false, guest: null })}>Cancel</button>
            <button className="btn-primary" onClick={saveGuest} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </Modal>

      {/* Import Modal */}
      <Modal isOpen={importModal} onClose={() => { setImportModal(false); setCsvFile(null); setCsvPreview([]); }} title="Import Guests from CSV">
        <p style={{ color: 'var(--c-fg-muted)', marginTop: 0, marginBottom: 20, fontSize: 14 }}>
          Upload a CSV file with columns: Name, WhatsApp Number, Email (optional). First row can be headers.
        </p>
        {tierUsage && !tierUsage.isAdmin && tierUsage.guests.max > 0 && (
          <p style={{ color: tierUsage.guests.remaining < 50 ? '#e09200' : 'var(--c-fg-muted)', marginTop: 0, marginBottom: 16, fontSize: 13, fontWeight: 500 }}>
            You can import up to {tierUsage.guests.remaining.toLocaleString()} more guests ({tierUsage.guests.used.toLocaleString()}/{tierUsage.guests.max.toLocaleString()} used).
          </p>
        )}
        <div style={{ border: '2px dashed var(--c-border)', borderRadius: 12, padding: 32, textAlign: 'center', marginBottom: 20 }}>
          <input type="file" accept=".csv" onChange={handleCsvFile} style={{ display: 'none' }} id="csv-upload" />
          <label htmlFor="csv-upload" style={{ cursor: 'pointer' }}>
            <Icon name="upload" size={32} />
            <div style={{ marginTop: 12, fontWeight: 500 }}>{csvFile ? csvFile.name : 'Click to upload CSV'}</div>
          </label>
        </div>
        {csvPreview.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Preview (first 5 rows):</div>
            <div style={{ background: 'var(--c-muted)', borderRadius: 8, padding: 12, fontSize: 13 }}>
              {csvPreview.map((row, i) => (
                <div key={i} style={{ padding: '4px 0' }}>{row.name} — {row.whatsappNumber || 'No phone'}</div>
              ))}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={() => { setImportModal(false); setCsvFile(null); setCsvPreview([]); }}>Cancel</button>
          <button className="btn-primary" onClick={importCsv} disabled={!csvFile || saving}>{saving ? 'Importing...' : 'Import'}</button>
        </div>
      </Modal>

      {/* Custom Message Modal (full-featured: media, scheduling, templates) */}
      <CustomMessageModal
        isOpen={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        event={event}
        guests={guests}
        preSelectedGuests={getPreSelectedGuests()}
      />

      <DeleteConfirmationModal
        show={!!deleteConfirm}
        onConfirm={() => deleteConfirm?.type === 'bulk' ? bulkDelete() : deleteGuest()}
        onCancel={() => setDeleteConfirm(null)}
        title={deleteConfirm?.type === 'bulk' ? 'Delete Selected Guests' : 'Delete Guest'}
        message={deleteConfirm?.type === 'bulk'
          ? `Are you sure you want to delete ${selectedIds.size} selected guests?`
          : undefined}
        itemName={deleteConfirm?.guest?.name}
        itemType="guest"
      />
    </>
  );
}

export default GuestsPage;
